import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  Form,
  Button,
  Input,
  Label,
  FormFeedback,
  FormGroup,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import classnames from 'classnames';
import { createOrUpdateUser } from '../../../actions/user/action';
import { alertError, alertSuccess } from '../../../helpers/errorHandling';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import InnerLoader from '../../../Components/Common/InnerLoader';
import ReactSelect from 'react-select';
import { useSelector } from 'react-redux';

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),
  roles: Yup.array()
    .min(1, 'At least one role is required')
    .required('Roles are required'),
});

const UserForm = ({
  handleClose = () => {},
  updateHandler = () => {},
  isFromPopup = false,
  subject = 'default',
  permissions = [],
  roles = [],
  user = null,
  brandId = null,
  storeId = null,
}) => {
  const { organizationTree, countries } = useSelector(
    (state) => state?.organization
  );
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('0');

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      roles: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const data = {
          input: {
            _id: values?._id || null,
            organizationId: organizationTree?.organizationId,
            firstName: values?.firstName,
            lastName: values?.lastName,
            email: values?.email,
            mobile: values?.mobile,
            roles: values?.roles?.map((r) => {
              return {
                roleId: r?.roleId,
                countries: r?.countries?.map((c) => c.value),
                name: r?.name,
                brandId: brandId,
                storeId: storeId,
                organizationId: organizationTree?.organizationId,
                extraPermissions: r?.extraPermissions?.map((p) => ({
                  permissionId: p.permissionId,
                  name: p.name,
                  action: p.action,
                  subject: p.subject,
                  conditions: p.conditions,
                })),
                deniedPermissions: r?.deniedPermissions?.map((p) => ({
                  permissionId: p.permissionId,
                  name: p.name,
                  action: p.action,
                  subject: p.subject,
                  conditions: p.conditions,
                })),
                permissions: r?.permissions?.map((p) => ({
                  permissionId: p.permissionId,
                  name: p.name,
                  action: p.action,
                  subject: p.subject,
                  conditions: p.conditions,
                })),
              };
            }),
          },
        };

        const res = await createOrUpdateUser(data);
        if (res?._id) {
          alertSuccess('Updated successfully');
          if (isFromPopup) {
            setTimeout(() => {
              resetForm();
              updateHandler();
              handleClose();
            }, 1000);
          }
        } else {
          alertError('Failed, Please try again later');
        }
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      } catch (e) {
        console.log(e);
        alertError('Failed, Please try again later');
      }
    },
  });

  const {
    handleSubmit,
    getFieldProps,
    setFieldValue,
    values,
    touched,
    errors,
    resetForm,
  } = formik;

  useEffect(() => {
    if (user) {
      const filteredRoles = user?.roles.map((role) => ({
        ...role,
        countries: countries
          ?.filter((c) => role?.countries?.includes(c.countryCode))
          .map((c) => ({ value: c.countryCode, label: c.name })),
        extraPermissions: role?.extraPermissions?.map((p) => ({
          label: `${p.name} ${p.subject}`,
          value: p.permissionId,
          permissionId: p.permissionId,
          name: p.name,
          action: p.action,
          subject: p.subject,
          conditions: p.conditions,
        })),
        deniedPermissions: role?.deniedPermissions?.map((p) => ({
          label: `${p.name} ${p.subject}`,
          value: p.permissionId,
          permissionId: p.permissionId,
          name: p.name,
          action: p.action,
          subject: p.subject,
          conditions: p.conditions,
        })),
      }));

      resetForm({
        values: {
          _id: user?._id,
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          email: user?.email || '',
          mobile: user?.mobile || '',
          roles: filteredRoles || [],
        },
      });
    }
  }, [user]);

  const addRole = () => {
    const newRoles = [
      ...values.roles,
      {
        roleId: '',
        name: '',
        countries: [],
        brandId: brandId,
        storeId: storeId,
        organizationId: organizationTree?.organizationId,
      },
    ];
    setFieldValue('roles', newRoles);
    setActiveTab((newRoles.length - 1).toString());
  };

  const removeRole = (index) => {
    const updated = [...values.roles];
    updated.splice(index, 1);
    setFieldValue('roles', updated);
    setActiveTab(Math.max(0, index - 1).toString());
  };

  return (
    <>
      <Form onSubmit={handleSubmit}>
        <div className="d-flex gap-3 flex-wrap">
          <FormGroup className="col">
            <Label for="firstName">First Name</Label>
            <Input
              id="firstName"
              {...getFieldProps('firstName')}
              invalid={touched.firstName && !!errors.firstName}
            />
            <FormFeedback>{errors.firstName}</FormFeedback>
          </FormGroup>
          <FormGroup className="col">
            <Label for="lastName">Last Name</Label>
            <Input
              id="lastName"
              {...getFieldProps('lastName')}
              invalid={touched.lastName && !!errors.lastName}
            />
            <FormFeedback>{errors.lastName}</FormFeedback>
          </FormGroup>
        </div>

        <div className="d-flex gap-3 flex-wrap">
          <FormGroup className="col">
            <Label for="email">Email</Label>
            <Input id="email" {...getFieldProps('email')} />
          </FormGroup>
          <FormGroup className="col">
            <Label for="mobile">Mobile</Label>
            <Input id="mobile" {...getFieldProps('mobile')} />
          </FormGroup>
        </div>

        {/* Tab Navigation */}
        <div className="d-flex justify-content-between align-items-center">
          <Nav tabs>
            {values.roles.map((role, index) => {
              const roleLabel =
                roles.find((r) => r.roleId === role.roleId)?.name ||
                `Role ${index + 1}`;
              return (
                <NavItem key={index}>
                  <NavLink
                    className={classnames({ active: activeTab === `${index}` })}
                    onClick={() => setActiveTab(`${index}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    {roleLabel}
                  </NavLink>
                </NavItem>
              );
            })}
          </Nav>
          <Button color="success" onClick={addRole} size="sm">
            + Add Role
          </Button>
        </div>

        {/* Tab Content */}
        <TabContent activeTab={activeTab} className="border border-top-0 p-3">
          {values.roles.map((role, index) => (
            <TabPane tabId={`${index}`} key={index}>
              <FormGroup>
                <Label>Role</Label>
                <ReactSelect
                  options={roles.map((r) => ({
                    value: r.roleId,
                    label: r.name,
                    permissions: r.permissions,
                  }))}
                  value={roles
                    .map((r) => ({
                      value: r.roleId,
                      label: r.name,
                      permissions: r.permissions,
                    }))
                    .find((opt) => opt.value === role.roleId)}
                  onChange={(selected) => {
                    setFieldValue(`roles[${index}].roleId`, selected.value);
                    setFieldValue(`roles[${index}].name`, selected.label);
                    setFieldValue(
                      `roles[${index}].permissions`,
                      selected.permissions
                    );
                  }}
                />
              </FormGroup>

              <FormGroup>
                <Label>Countries</Label>
                <ReactSelect
                  isMulti
                  options={countries.map((c) => ({
                    value: c.countryCode,
                    label: c.name,
                  }))}
                  value={role.countries}
                  onChange={(selected) =>
                    setFieldValue(`roles[${index}].countries`, selected)
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label>Extra Permissions</Label>
                <ReactSelect
                  isMulti
                  options={permissions.map((p) => ({
                    value: p._id,
                    label: `${p.name} ${p.subject}`,
                    ...p,
                  }))}
                  value={role.extraPermissions}
                  onChange={(selected) =>
                    setFieldValue(`roles[${index}].extraPermissions`, selected)
                  }
                  className="text-capitalize"
                />
              </FormGroup>

              <FormGroup>
                <Label>Denied Permissions</Label>
                <ReactSelect
                  isMulti
                  options={permissions.map((p) => ({
                    value: p._id,
                    label: `${p.name} ${p.subject}`,
                    ...p,
                  }))}
                  value={role.deniedPermissions}
                  onChange={(selected) =>
                    setFieldValue(`roles[${index}].deniedPermissions`, selected)
                  }
                  className="text-capitalize"
                />
              </FormGroup>

              <Button
                color="danger"
                size="sm"
                onClick={() => removeRole(index)}
              >
                Remove Role
              </Button>
            </TabPane>
          ))}
        </TabContent>

        <Button type="submit" color="primary" className="mt-4">
          Submit
        </Button>
      </Form>
      {/* <ToastContainer /> */}
      {loading && <InnerLoader />}
    </>
  );
};

export default UserForm;
